import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const useCounter = (end, duration = 2000, isVisible) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const easeOut  = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(end);
    };
    requestAnimationFrame(step);
  }, [end, duration, isVisible]);
  return count;
};

export default function StatCard({ icon: Icon, number, suffix = '', label, delay = 0 }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref   = useRef(null);
  const count = useCounter(number, 2000, isVisible);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => { if (ref.current) obs.unobserve(ref.current); };
  }, []);

  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 30 }} animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="bg-white rounded-2xl p-8 flex flex-col items-start shadow-sm hover:shadow-md border border-blue-100 hover:border-[#2563EB]/30 transition-all duration-300 group hover:-translate-y-1">
      {Icon && (
        <div className="w-12 h-12 bg-[#2563EB] rounded-xl flex items-center justify-center mb-5 shadow-sm group-hover:bg-[#1D4ED8] transition-colors">
          <Icon className="w-6 h-6 text-white" />
        </div>
      )}
      <div className="text-4xl sm:text-5xl font-black text-[#2563EB] font-mono mb-2">
        {count.toLocaleString()}{suffix}
      </div>
      <p className="font-bold text-xs uppercase tracking-widest text-slate-400 group-hover:text-[#2563EB] transition-colors">
        {label}
      </p>
    </motion.div>
  );
}
